import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { DashboardLayout } from "@/components/DashboardLayout";
import { LoadingBlock, EmptyState } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { formatDate, REQUEST_STATUS_LABELS, euro } from "@/lib/format";
import { BellRing, UserPlus } from "lucide-react";

const UNTAKEN = ["inviata", "in_valutazione"];

export default function GestioneRichieste() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-requests"], queryFn: async () => (await api.get("/admin/requests")).data });
  const requests = data?.requests || [];
  const [assignReq, setAssignReq] = useState(null);

  const remind = async (id) => {
    try {
      const r = await api.post(`/admin/requests/${id}/remind`);
      toast.success(`Sollecito inviato a ${r.data.notified} medico/i disponibili.`);
      qc.invalidateQueries({ queryKey: ["admin-requests"] });
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <DashboardLayout title="Gestione richieste">
      {isLoading ? <LoadingBlock /> : requests.length === 0 ? <EmptyState title="Nessuna richiesta" /> : (
        <div className="overflow-x-auto bg-white rounded-2xl border border-[#D2E3DB]">
          <Table>
            <TableHeader><TableRow><TableHead>Servizio</TableHead><TableHead>Comune</TableHead><TableHead>Data</TableHead><TableHead>Urgenza</TableHead><TableHead>Tariffa</TableHead><TableHead>Stato</TableHead><TableHead>Azioni</TableHead></TableRow></TableHeader>
            <TableBody>
              {requests.map((r) => (
                <TableRow key={r.id} data-testid={`admin-req-${r.id}`}>
                  <TableCell className="font-semibold text-[#102A2A]">{r.service_name}{r.description ? <span className="block text-xs font-normal text-[#5C6B68] max-w-[220px] truncate" title={r.description}>{r.description}</span> : null}</TableCell>
                  <TableCell className="text-[#5C6B68]">{r.municipality}</TableCell>
                  <TableCell className="text-[#5C6B68]">{formatDate(r.preferred_date)}</TableCell>
                  <TableCell>{r.urgency === "prioritaria" ? <span className="text-[#8a6400] font-semibold">Prioritaria</span> : "Normale"}</TableCell>
                  <TableCell className="text-[#087A73] font-semibold">{r.agreed_price_cents != null ? euro(r.agreed_price_cents) : "—"}</TableCell>
                  <TableCell><StatusBadge status={r.status} label={REQUEST_STATUS_LABELS[r.status]} /></TableCell>
                  <TableCell>
                    {UNTAKEN.includes(r.status) ? (
                      <div className="flex gap-2">
                        <Button data-testid={`remind-${r.id}`} onClick={() => remind(r.id)} variant="outline" className="h-9 px-3 rounded-xl border-2 border-[#087A73] text-[#087A73] hover:bg-[#087A73]/10"><BellRing className="h-4 w-4 mr-1" /> Sollecita</Button>
                        <Button data-testid={`assign-${r.id}`} onClick={() => setAssignReq(r)} className="h-9 px-3 rounded-xl bg-[#087A73] hover:bg-[#06615B] text-white"><UserPlus className="h-4 w-4 mr-1" /> Assegna</Button>
                      </div>
                    ) : <span className="text-xs text-[#5C6B68]">—</span>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <AssignDialog req={assignReq} onClose={() => setAssignReq(null)} onDone={() => { setAssignReq(null); qc.invalidateQueries({ queryKey: ["admin-requests"] }); }} />
    </DashboardLayout>
  );
}

function AssignDialog({ req, onClose, onDone }) {
  const [pid, setPid] = useState("");
  const [saving, setSaving] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["req-candidates", req?.id],
    queryFn: async () => (await api.get(`/admin/requests/${req.id}/candidates`)).data,
    enabled: !!req,
  });
  const candidates = data?.candidates || [];

  const assign = async () => {
    if (!pid) return toast.error("Seleziona un medico.");
    setSaving(true);
    try {
      await api.post(`/admin/requests/${req.id}/assign`, { professional_id: pid });
      toast.success("Richiesta assegnata al medico.");
      setPid("");
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-white" data-testid="assign-dialog">
        <DialogHeader><DialogTitle>Assegna richiesta a un medico</DialogTitle></DialogHeader>
        {req && (
          <div className="space-y-4">
            <p className="text-sm text-[#5C6B68]"><strong className="text-[#102A2A]">{req.service_name}</strong> · {req.municipality}{req.description ? ` · ${req.description}` : ""}</p>
            {isLoading ? <LoadingBlock /> : candidates.length === 0 ? (
              <div data-testid="no-candidates" className="text-sm bg-[#FBF0D6] border border-[#D99A00]/40 text-[#8a6400] rounded-xl px-4 py-3">Nessun medico verificato disponibile per questa prestazione e comune. Usa "Sollecita" o invita nuovi medici.</div>
            ) : (
              <Select value={pid} onValueChange={setPid}>
                <SelectTrigger data-testid="assign-select" className="h-12 rounded-xl bg-white"><SelectValue placeholder="Seleziona un medico" /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">
                  {candidates.map((c) => <SelectItem key={c.professional_id} value={c.professional_id} data-testid={`cand-${c.professional_id}`}>{c.name}{c.profession ? ` · ${c.profession}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-xl border-2 border-[#D2E3DB]">Annulla</Button>
          <Button data-testid="assign-confirm" onClick={assign} disabled={saving || !pid} className="rounded-xl bg-[#087A73] hover:bg-[#06615B] text-white">Assegna</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
